import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import clsx from 'clsx';
import VCImage from '@components/atoms/vc-images/vc-image';
import Icon from '@components/atoms/icon/icon';
import { CarouselDataSource, CatalogPageHeaderData } from '@interfaces/api/responses/catalog-page-metadata-response';
import { useAnalytics } from '@context/analytics.context';
import CmsImages from '@components/atoms/vc-images/components/cms-image';
import Environment from '@config/environment';
import styles from './catalog-header-navigation.module.scss';

type CatalogHeaderProps = {
  dataPage?: CatalogPageHeaderData;
  carouselDataSource?: CarouselDataSource;
} & React.HTMLProps<HTMLDivElement>;

const CatalogHeaderNavigation: React.FC<CatalogHeaderProps> = (props): React.JSX.Element => {
  const { dataPage, carouselDataSource } = props;

  const { t } = useTranslation();
  const { sendAnalyticEvent: sendTopNavEvent } = useAnalyticEvents('top_navigation');
  const { scriptsEventData } = useAnalytics();
  const imageType = carouselDataSource === 'cms' || carouselDataSource === 'cmsv2' ? 'brandNav' : 'default';

  useEffect(() => {
    if (scriptsEventData?.isTcEvents1Loaded && scriptsEventData?.isTcEvents7Loaded) {
      sendTopNavEvent('top_navigation_view');
    }
  }, [scriptsEventData?.isTcEvents1Loaded, scriptsEventData?.isTcEvents7Loaded]);

  return (
    <Fragment>
      {dataPage?.carousel?.length > 0 && (
        <span className={styles.headerNavigation__title}>{t('CATALOG.TOPNAV.TITLE')}</span>
      )}
      <div className={styles.headerNavigationContainer}>
        <div
          className={clsx(styles.headerNavigation, {
            [styles['headerNavigation--brandCategory']]: !dataPage?.isMainPage,
          })}
          data-cy="guided_search_main"
        >
          {dataPage?.carousel?.map((item) => {
            const blockName =
              item?.text
                .toLowerCase()
                .split(' ')
                .filter((word) => word.length > 1)
                .join('_') || '';
            return (
              <a
                href={item.url.path}
                onClick={() => sendTopNavEvent('top_navigation_click', { property: blockName })}
                className={styles.headerNavigation__item}
                key={item?.url?.url}
                data-cy="guided_search_part"
              >
                {dataPage?.isMainPage && (
                  <>
                    {imageType === 'brandNav' ? (
                      <CmsImages
                        className={styles.headerNavigation__item__brandImg}
                        src={
                          carouselDataSource === 'cms'
                            ? `${Environment.vccmsimguri}${item.picture.path}`
                            : item?.picture?.url?.url
                        }
                        // width and height of images that should be received - values may change upon results of testing
                        height={304}
                        width={710}
                        alt={item.text}
                        type="edito"
                        priority
                        disableLazyLoad
                      />
                    ) : (
                      <VCImage
                        className={styles.headerNavigation__item__brandImg}
                        src={item.picture.path}
                        // width and height of images that should be received - values may change upon results of testing
                        height={304}
                        width={710}
                        alt={item.text}
                        priority
                        disableLazyLoad
                      />
                    )}
                  </>
                )}
                <div className={styles.headerNavigation__item__title}>
                  {!dataPage?.isMainPage && (
                    <div className={styles.headerNavigation__item__smallImg}>
                      {imageType === 'brandNav' ? (
                        <CmsImages
                          src={
                            carouselDataSource === 'cms'
                              ? `${Environment.vccmsimguri}${item.picture.path}`
                              : item?.picture?.url?.url
                          }
                          height={48}
                          width={48}
                          alt={item.text}
                          type="edito"
                          priority
                          disableLazyLoad
                        />
                      ) : (
                        <VCImage
                          src={item.picture.path}
                          height={48}
                          width={48}
                          alt={item.text}
                          disableLazyLoad
                          priority
                        />
                      )}
                    </div>
                  )}
                  <span className={styles.headerNavigation__item__title__text}>{item.text}</span>
                  {dataPage?.isMainPage && (
                    <Icon
                      name="arrow-right-long-20"
                      className={styles.arrowIcon}
                    />
                  )}
                </div>
              </a>
            );
          })}
          <span className={styles['headerNavigation__item--fakePadding']} />
        </div>
      </div>
    </Fragment>
  );
};

export default CatalogHeaderNavigation;
